import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from './LoginModal';
import { useSuperApp } from '../hooks/useSuperApp';
import { getToken, setToken, setRefreshToken, attemptTokenRefresh } from '../api/client';
import { isJwtExpired, decodeJwtPayload } from '../utils/jwtUtils';
import { authenticateByTestAccess } from '../api/services/testCandidate';

const TEST_ACCESS_KEY = 'diil_test_access_token';

/**
 * Try refreshing the JWT. Uses refresh token first, falls back to
 * test-access UUID re-auth (for users who entered via /test/:uuid link
 * and may not have a refresh token from the backend).
 * @returns {boolean} true if a new JWT was obtained
 */
const refreshWithFallback = async () => {
  // 1. Try standard refresh token
  const newToken = await attemptTokenRefresh();
  if (newToken) return true;
  
  // 2. Fallback: re-authenticate with stored test access UUID
  const testAccessToken = localStorage.getItem(TEST_ACCESS_KEY);
  if (testAccessToken) {
    try {
      const { data } = await authenticateByTestAccess(testAccessToken);
      if (data?.accessToken) {
        setToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken);
        return true;
      }
    } catch (err) {
      console.error('Test access re-auth failed:', err);
    }
  }
  
  return false;
};

/**
 * AuthGuard Component
 *
 * Listens to global auth events (401/403) and handles re-authentication.
 * For super app users (Toki/Hambi): silently re-authenticates, never shows phone login.
 * For direct test access users: silently re-authenticates with stored UUID.
 * For direct visitors: shows the phone login modal.
 *
 * Also manages proactive token refresh to keep sessions alive for 24+ hours:
 * - Periodic check every 4 minutes, refreshes if token expiring within 5 min
 * - Visibility change handler for mobile background/foreground transitions
 *
 * Events handled:
 * - auth:sessionExpired (401 after token refresh failed)
 * - auth:forbidden (403 access denied)
 * - auth:loginRequired (protected route access, includes redirectTo URL)
 */
const AuthGuard = ({ children }) => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // Analytics only: why the modal opened, and the job when it was a "Чатлах" click
  const [loginContext, setLoginContext] = useState({ trigger: null, assessmentId: null, companyId: null });
  const redirectUrlRef = useRef(null);
  const isRetryingRef = useRef(false);
  const { isSuperAppUser, retryAuth } = useSuperApp();

  // Handle auth events that require re-login
  const handleAuthRequired = useCallback(async (redirectTo = null) => {
    // Prevent concurrent retry attempts
    if (isRetryingRef.current) return;
    isRetryingRef.current = true;

    try {
      // 1. Super app users — silently re-authenticate with Toki/Hambi token
      if (isSuperAppUser) {
        const success = await retryAuth();
        if (success && redirectTo) navigate(redirectTo);
        // Never show phone modal for super app users
        return;
      }

      // 2. Test access users — silently re-authenticate with stored UUID
      const testAccessToken = localStorage.getItem(TEST_ACCESS_KEY);
      if (testAccessToken) {
        try {
          const { data } = await authenticateByTestAccess(testAccessToken);
          if (data?.accessToken) {
            setToken(data.accessToken);
            if (data.refreshToken) setRefreshToken(data.refreshToken);
            if (redirectTo) navigate(redirectTo);
            return;
          }
        } catch (err) {
          console.error('Test access re-auth failed:', err);
        }
      }

      // 3. Direct visitors — show phone login modal
      redirectUrlRef.current = redirectTo;
      const target = String(redirectTo || "");
      const targetParams = new URLSearchParams(target.split("?")[1] || "");
      setLoginContext({
        trigger: target.startsWith("/chat") ? "interview" : target ? "protected_route" : "session_expired",
        assessmentId: targetParams.get("jobId"),
        companyId: targetParams.get("companyId"),
      });
      setIsLoginModalOpen(true);
    } finally {
      isRetryingRef.current = false;
    }
  }, [isSuperAppUser, retryAuth, navigate]);

  // Listen for auth events
  useEffect(() => {
    const handleSessionExpired = () => {
      // Session expired — try to recover silently
      handleAuthRequired(null);
    };

    const handleForbidden = () => {
      // 403 = user lacks permission, don't prompt re-login
    };

    const handleLoginRequired = (event) => {
      const redirectTo = event.detail?.redirectTo;
      handleAuthRequired(redirectTo);
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    window.addEventListener('auth:forbidden', handleForbidden);
    window.addEventListener('auth:loginRequired', handleLoginRequired);

    return () => {
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
      window.removeEventListener('auth:forbidden', handleForbidden);
      window.removeEventListener('auth:loginRequired', handleLoginRequired);
    };
  }, [handleAuthRequired]);

  // Proactive token refresh — keeps session alive for 24+ hours
  useEffect(() => {
    const TOKEN_CHECK_INTERVAL = 4 * 60 * 1000; // Check every 4 minutes
    const TOKEN_REFRESH_BUFFER = 5 * 60; // Refresh if expiring within 5 minutes (seconds)

    const checkAndRefreshToken = async () => {
      const token = getToken();
      if (!token) return;

      const payload = decodeJwtPayload(token);
      if (!payload?.exp) return;

      const secondsUntilExpiry = payload.exp - Math.floor(Date.now() / 1000);
      if (secondsUntilExpiry < TOKEN_REFRESH_BUFFER) {
        try {
          await refreshWithFallback();
        } catch (err) {
          console.error('Proactive token refresh failed:', err);
        }
      }
    };

    checkAndRefreshToken();
    const intervalId = setInterval(checkAndRefreshToken, TOKEN_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // Refresh token when app returns from background (mobile)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const token = getToken();
      if (!token) return;

      let needsRefresh = false;

      if (isJwtExpired(token)) {
        needsRefresh = true;
      } else {
        const payload = decodeJwtPayload(token);
        if (payload?.exp) {
          const secondsUntilExpiry = payload.exp - Math.floor(Date.now() / 1000);
          needsRefresh = secondsUntilExpiry < 5 * 60;
        }
      }

      if (needsRefresh) {
        try {
          await refreshWithFallback();
        } catch (err) {
          console.error('Background-return token refresh failed:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Handle successful login
  const handleLoginSuccess = useCallback(() => {
    setIsLoginModalOpen(false);

    // If we have a redirect URL, navigate there (SPA navigation, no refresh)
    if (redirectUrlRef.current) {
      const redirectTo = redirectUrlRef.current;
      redirectUrlRef.current = null;
      navigate(redirectTo);
    }
    // No else - user stays on current page after login
  }, [navigate]);

  // Handle modal close (user dismissed without login)
  const handleModalClose = useCallback(() => {
    setIsLoginModalOpen(false);
    redirectUrlRef.current = null;
  }, []);

  return (
    <>
      {children}
      {/* Never render LoginModal for super app users */}
      {!isSuperAppUser && (
        <LoginModal
          isOpen={isLoginModalOpen}
          trigger={loginContext.trigger}
          assessmentId={loginContext.assessmentId}
          companyId={loginContext.companyId}
          onClose={handleModalClose}
          onSuccess={handleLoginSuccess}
        />
      )}
    </>
  );
};

export default AuthGuard;
